import { createSlice } from '@reduxjs/toolkit';
import sustainabilityApi from '../apis/sustainability.api';
import type { SustainabilitySectionDto } from '../../models/sustainability';

const initialState: {
	sectionsByProfileType: Record<number, SustainabilitySectionDto[]>;
} = {
	sectionsByProfileType: {}
};

const profileSectionsSlice = createSlice({
	name: 'profileSections',
	initialState,
	reducers: {
		clearProfileSections(state, action: { payload?: number }) {
			const profileType = action.payload;
			if (profileType === undefined) {
				state.sectionsByProfileType = {};
			} else {
				delete state.sectionsByProfileType[profileType];
			}
		},
		setProfileSections(
			state,
			action: { payload: { profileType: number; sections: SustainabilitySectionDto[] } }
		) {
			const { profileType, sections } = action.payload;
			state.sectionsByProfileType[profileType] = sections;
		}
	},
	extraReducers: (builder) => {
		builder.addMatcher(
			sustainabilityApi.endpoints.getProfileSections.matchFulfilled,
			(state, action) => {
				const profileType = action.meta.arg.originalArgs;
				const sections = action.payload;
				state.sectionsByProfileType[profileType] = sections;
			}
		);
	}
});

export const { clearProfileSections, setProfileSections } = profileSectionsSlice.actions;

type ProfileSectionsState = {
	profileSections?: { sectionsByProfileType?: Record<number, SustainabilitySectionDto[]> };
};

export const selectProfileSectionsByProfileType = (
	state: ProfileSectionsState,
	profileType: number
): SustainabilitySectionDto[] => state.profileSections?.sectionsByProfileType?.[profileType] ?? [];

export const selectPreviousNextProfileSections = (
	state: ProfileSectionsState,
	profileType: number,
	section: string
): { previous: string; next: string } => {
	const sustainabilitySections: {
		previous: string;
		next: string;
	} = { previous: '', next: '' };

	const sections = state.profileSections?.sectionsByProfileType?.[profileType] ?? [];

	const sectionIndex = sections.findIndex((x) => x.name === section);

	sustainabilitySections.previous = sectionIndex > 0 ? sections[sectionIndex - 1].name : '';

	sustainabilitySections.next =
		sectionIndex < sections.length - 1 ? sections[sectionIndex + 1].name : '';

	return sustainabilitySections;
};

export default profileSectionsSlice.reducer;
